import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { authService } from '../services/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const posthog = usePostHog();
    const [user, setUser] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadUser();
    }, []);

    const loadUser = async () => {
        try {
            // Try to get current user (works for both JWT and OAuth)
            // For JWT: token is in localStorage and added to request headers
            // For OAuth: tokens are in HttpOnly cookies and sent automatically
            const data = await authService.getCurrentUser();
            setUser(data.user);
            setWorkspaces(data.workspaces);

            if (posthog && data.user) {
                posthog.identify(data.user.id, {
                    email: data.user.email,
                    name: data.user.fullName || `${data.user.firstName} ${data.user.lastName}`
                });
            }

            // Check for pending invitation state (from OAuth or reload)
            const pendingInvitation = localStorage.getItem('pendingInvitation');
            if (pendingInvitation) {
                try {
                    const { returnTo } = JSON.parse(pendingInvitation);
                    localStorage.removeItem('pendingInvitation'); // Clear immediately to prevent loops

                    // Redirect to invitation page if not already there
                    if (window.location.pathname !== returnTo) {
                        window.location.href = returnTo;
                    }
                } catch (e) {
                    console.error('Error parsing pending invitation:', e);
                    localStorage.removeItem('pendingInvitation');
                }
            }
        } catch (error) {
            console.error('Failed to load user:', error);
            // User is not authenticated - this is normal on first visit
            // Don't call logout here - it would clear OAuth cookies unnecessarily
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        const data = await authService.login(email, password);
        setUser(data.user);
        setWorkspaces(data.workspaces);
        if (posthog && data.user) {
            posthog.identify(data.user.id, {
                email: data.user.email,
                name: data.user.fullName || `${data.user.firstName} ${data.user.lastName}`
            });
        }
        return data;
    };

    const register = async (userData) => {
        const data = await authService.register(userData);
        setUser(data.user);
        setWorkspaces([data.workspace]);
        if (posthog && data.user) {
            posthog.identify(data.user.id, {
                email: data.user.email,
                name: data.user.fullName || `${data.user.firstName} ${data.user.lastName}`
            });
        }
        return data;
    };

    const logout = async () => {
        if (posthog) {
            posthog.reset();
        }
        setUser(null);
        setWorkspaces([]);
        await authService.logout();
    };

    const value = {
        user,
        workspaces,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        refreshUser: loadUser
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
